import { forwardRef, useRef, type ComponentPropsWithoutRef } from 'react';
import { Input } from '@roger/ui-components';
import {
  formatDollarAmount,
  isValidDollarInput,
  stripCurrencyFormatting,
} from '../utils/currencyUtils';

type CurrencyInputProps = Omit<
  ComponentPropsWithoutRef<typeof Input>,
  'value' | 'onChange' | 'type' | 'inputMode'
> & {
  value: string;
  onValueChange: (next: string) => void;
};

/**
 * Whether an edit is allowed to land. Normally the result has to still look
 * like a dollar amount, but a stored value can be something this input would
 * never let you type — a legacy charge with three decimals, exponent notation
 * — and then every backspace produces another invalid string, so the field
 * looks frozen and save stays blocked on a value the biller cannot correct.
 * While the current value is already invalid, any edit that shortens it is
 * allowed, which is enough to delete back to a valid amount.
 */
const acceptsEdit = (current: string, next: string): boolean =>
  isValidDollarInput(next) ||
  (!isValidDollarInput(current) && next.length < current.length);

/**
 * Dollar-amount text input: keystrokes that would make the value stop being a
 * dollar amount are dropped, and blur normalizes what the user typed to two
 * decimals ("100" → "100.00", "12." → "12.00"). Blank stays blank so a caller
 * can tell "not entered" from "0.00".
 *
 * Lives in the app rather than the design system because the currency rules it
 * enforces live in the app's currencyUtils, and it adds behavior rather than a
 * visual variant.
 */
export const CurrencyInput = forwardRef<HTMLInputElement, CurrencyInputProps>(
  function CurrencyInput(
    { value, onValueChange, onBlur, onFocus, ...props },
    ref
  ) {
    // Normalizing on every blur would rewrite stored amounts the user merely
    // tabbed through — the backend emits plain Decimals like "0" and "112.5" —
    // marking the form dirty and the saved preview stale with no edit behind
    // it. Only a keystroke earns the normalization.
    const editedRef = useRef(false);
    return (
      <Input
        {...props}
        ref={ref}
        type='text'
        inputMode='decimal'
        value={value}
        onFocus={event => {
          editedRef.current = false;
          onFocus?.(event);
        }}
        onChange={event => {
          const next = stripCurrencyFormatting(event.target.value);
          if (!acceptsEdit(value, next)) return;
          editedRef.current = true;
          onValueChange(next);
        }}
        onBlur={event => {
          if (editedRef.current) {
            editedRef.current = false;
            const formatted = formatDollarAmount(value);
            if (formatted !== value) onValueChange(formatted);
          }
          onBlur?.(event);
        }}
      />
    );
  }
);
